'use client';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Heading } from '../ui/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

export function PartnerCertification({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: TDynamicData[];
}) {
  const { t } = useTranslations();

  const isDesktop = useBreakpoint('sm');

  return (
    <section
      className={cn('custom-section', 'bg-[#F2EDE6] pb-8 pt-8 sm:pb-[50px] sm:pt-12 lg:pt-[70px]')}
    >
      <div
        className={cn(
          'custom-container',
          'relative flex flex-col items-start justify-start gap-8 overflow-hidden rounded-[16px] bg-[#F2EDE6] p-6 sm:p-7 md:flex-row md:items-center md:justify-between lg:gap-12 lg:p-8',
        )}
      >
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 md:max-w-[539px]">
          <Heading title={title} className={{ container: 'hidden md:flex', vector: '-bottom-4' }} />
          <Heading title={title} tag="h4" className={{ container: 'flex md:hidden' }} />
          <Paragraph className="w-full text-sm text-foreground sm:text-base lg:text-lg">
            {description}
          </Paragraph>
        </div>
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 sm:gap-6 md:max-w-[602px]">
          <strong className="text-lg font-medium tracking-tight text-foreground sm:text-xl lg:text-2xl">
            {t('text3537')}:
          </strong>
          <ul className="flex w-full flex-col items-start justify-start gap-2 sm:gap-4">
            {items.map((item, index) => (
              <li key={index} className="flex w-full items-center justify-start gap-3">
                <Image
                  key={item.name}
                  src={'/images/check-partner.svg'}
                  alt={item.name}
                  width={0}
                  height={0}
                  className="max-h-6 min-h-6 min-w-6 max-w-6 lg:max-h-[28px] lg:min-h-[28px] lg:min-w-[28px] lg:max-w-[28px]"
                />
                <p className="text-sm font-medium text-foreground sm:text-base lg:text-lg">
                  {item.name}
                </p>
              </li>
            ))}
          </ul>
        </div>
        <Image
          src={'/images/form-overlay.png'}
          alt="Overlay"
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center opacity-20 mix-blend-darken"
        />
      </div>
    </section>
  );
}
